import { useRef, useState, type ReactNode } from 'react';
import { PulseDot } from '../design-system';
import { cn } from '../../lib/utils';
import { swipeTarget } from '../../lib/mobileBoard';

export interface BoardStage {
  id: string;
  label: string;
  icon: string;
  /** Text colour class for the active tab. */
  text: string;
  /** Count-badge classes. */
  chip: string;
  count: number;
  empty: string;
  cards: ReactNode;
}

/**
 * Phone version of the trips board: one stage at a time, picked from tabs or by
 * swiping, instead of four side-by-side columns. Layout and tab logic only; the
 * dashboard supplies the cards.
 */
export function MobileTripBoard({ stages, initial, footer }: { stages: BoardStage[]; initial: string; footer?: ReactNode }) {
  const [active, setActive] = useState(initial);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const ids = stages.map((s) => s.id);
  const current = stages.find((s) => s.id === active) ?? stages[0];

  function onTouchEnd(e: React.TouchEvent) {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const next = swipeTarget(t.clientX - touch.current.x, t.clientY - touch.current.y, ids, current.id);
    touch.current = null;
    if (next) setActive(next);
  }

  return (
    <div>
      <div role="tablist" aria-label="Trip stages" className="sticky top-0 z-10 -mx-5 mb-3 flex gap-1 bg-app-bg px-5 pb-2 pt-1">
        {stages.map((s) => {
          const on = s.id === current.id;
          return (
            <button
              key={s.id}
              role="tab"
              aria-selected={on}
              type="button"
              onClick={() => setActive(s.id)}
              className={cn(
                'flex min-h-[48px] flex-1 flex-col items-center justify-center gap-0.5 rounded-[10px] border px-1 text-caption font-medium transition-colors',
                on ? cn('border-app-border bg-app-card', s.text) : 'border-transparent text-app-muted',
                !on && s.count === 0 && 'opacity-50',
              )}
            >
              <span className="flex items-center gap-1">
                {s.id === 'active' && s.count > 0 ? <PulseDot /> : <i className={cn('ti text-footnote', s.icon)} aria-hidden="true" />}
                <span className={cn('rounded-pill border px-1.5 text-caption leading-[16px]', s.chip)}>{s.count}</span>
              </span>
              <span className="max-w-full truncate">{s.label}</span>
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        onTouchStart={(e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
        onTouchEnd={onTouchEnd}
        className="flex min-h-[40vh] flex-col gap-3 pb-6"
      >
        {current.count === 0
          ? <p className="px-3 py-10 text-center text-footnote text-app-muted">{current.empty}</p>
          : current.cards}
      </div>
      {footer}
    </div>
  );
}
